// AudioManager.ts - Centralized audio system for Pocket Mustang Preserve v3.0
// SIMPLIFIED: Only night_song loops as permanent background music

export type SFXType = 'card_use' | 'horse' | 'coin' | 'year' | 'farrier' | 'good_ending';

const STORAGE_KEY_MUSIC = 'pmp_music_volume';
const STORAGE_KEY_SFX = 'pmp_sfx_volume';
const STORAGE_KEY_MUTED = 'pmp_muted';

export class AudioManager {
  private scene: Phaser.Scene;
  private currentMusic: Phaser.Sound.BaseSound | null = null;
  private musicVolume: number = 0.3;
  private sfxVolume: number = 0.5;
  private isMuted: boolean = false;
  private static instance: AudioManager | null = null;
  private musicStarted: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    
    // Stop any existing music from previous game instance
    if (AudioManager.instance) {
      AudioManager.instance.stopMusic();
    }
    AudioManager.instance = this;
    
    this.loadPreferences();
  }

  private loadPreferences(): void {
    try {
      const musicVol = localStorage.getItem(STORAGE_KEY_MUSIC);
      const sfxVol = localStorage.getItem(STORAGE_KEY_SFX);
      const muted = localStorage.getItem(STORAGE_KEY_MUTED);

      if (musicVol !== null) this.musicVolume = Math.max(0, Math.min(1, parseFloat(musicVol)));
      if (sfxVol !== null) this.sfxVolume = Math.max(0, Math.min(1, parseFloat(sfxVol)));
      if (muted !== null) this.isMuted = muted === 'true';
      
      console.log(`Audio loaded: music=${this.musicVolume}, sfx=${this.sfxVolume}, muted=${this.isMuted}`);
    } catch (e) {
      console.warn('LocalStorage not available for audio preferences');
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(STORAGE_KEY_MUSIC, this.musicVolume.toString());
      localStorage.setItem(STORAGE_KEY_SFX, this.sfxVolume.toString());
      localStorage.setItem(STORAGE_KEY_MUTED, this.isMuted.toString());
      console.log(`Audio saved: music=${this.musicVolume}, sfx=${this.sfxVolume}, muted=${this.isMuted}`);
    } catch (e) {
      console.warn('LocalStorage not available');
    }
  }

  // Start the permanent background music (night_song only)
  startBackgroundMusic(): void {
    if (this.musicStarted || this.currentMusic?.isPlaying) return;
    
    if (!this.scene.cache.audio.exists('night_song')) {
      console.warn('night_song audio not found');
      return;
    }

    const targetVolume = this.isMuted ? 0 : this.musicVolume;

    this.currentMusic = this.scene.sound.add('night_song', {
      volume: 0,
      loop: true
    });

    this.currentMusic.play();
    this.musicStarted = true;
    
    // Smooth fade in
    this.scene.tweens.add({
      targets: this.currentMusic,
      volume: targetVolume,
      duration: 2000,
      ease: 'Sine.easeIn'
    });
    
    console.log('Background music started');
  }

  playSFX(sfx: SFXType): void {
    if (this.isMuted) return;
    
    if (!this.scene.cache.audio.exists(sfx)) {
      console.warn(`SFX ${sfx} not found`);
      return;
    }

    this.scene.sound.play(sfx, { volume: this.sfxVolume });
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (this.currentMusic && !this.isMuted) {
      (this.currentMusic as any).volume = this.musicVolume;
    }
    this.savePreferences();
  }

  setSFXVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.savePreferences();
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  getSFXVolume(): number {
    return this.sfxVolume;
  }

  toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    
    if (this.currentMusic) {
      (this.currentMusic as any).volume = this.isMuted ? 0 : this.musicVolume;
    }
    
    this.savePreferences();
    return this.isMuted;
  }

  getMuted(): boolean {
    return this.isMuted;
  }

  stopMusic(): void {
    this.musicStarted = false;
    if (this.currentMusic) {
      try {
        this.currentMusic.stop();
        this.currentMusic.destroy();
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.currentMusic = null;
    }
  }
  
  // Stop all sounds in the scene (for game restart)
  stopAllSounds(): void {
    this.stopMusic();
    this.scene.sound.stopAll();
  }

  destroy(): void {
    this.stopMusic();
  }
}
